import {
  HydratedInterface,
  useSupportedAssets,
  useInterfaceActionPermissions,
} from "@zapier/client";
import {
  KebabMenuItem,
  useEmitInterfaceKebabInteractionEvent,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import { InterfaceModal } from "./InterfacesModals";
import { useCallback, useMemo } from "react";

interface UseInterfaceRowInput {
  interfaceObj: HydratedInterface;
  setCurrentModal: (modal?: InterfaceModal) => void;
  setCurrentInterface: (interfaceItem?: HydratedInterface) => void;
  onCopyToAnotherAccount?: (asset: HydratedInterface) => void;
}

export function useInterfaceRow({
  interfaceObj,
  setCurrentModal,
  setCurrentInterface,
  onCopyToAnotherAccount,
}: UseInterfaceRowInput) {
  const { isMPFEnabled } = useSupportedAssets();
  const permissions = useInterfaceActionPermissions(interfaceObj.id);
  const { emit } = useEmitInterfaceKebabInteractionEvent();
  const tAction = useTranslations("buttons");

  const handleDeleteInterface = useCallback(() => {
    setCurrentInterface(interfaceObj);
    setCurrentModal(InterfaceModal.Delete);
    emit("delete", interfaceObj.id);
  }, [interfaceObj, setCurrentInterface, setCurrentModal, emit]);

  const handleMoveInterface = useCallback(() => {
    setCurrentInterface(interfaceObj);
    setCurrentModal(InterfaceModal.Move);
    emit("move", interfaceObj.id);
  }, [interfaceObj, setCurrentInterface, setCurrentModal, emit]);

  const handleRenameInterface = useCallback(() => {
    setCurrentInterface(interfaceObj);
    setCurrentModal(InterfaceModal.Rename);
    emit("rename", interfaceObj.id);
  }, [interfaceObj, setCurrentInterface, setCurrentModal, emit]);

  // const handleDuplicateInterface = () => {
  //   setCurrentInterface(interfaceObj);
  //   setCurrentModal(InterfaceModal.Duplicate);
  //   emit("duplicate", interfaceObj.id);
  // };

  const handleChangeInterfaceOwner = useCallback(() => {
    setCurrentInterface(interfaceObj);
    setCurrentModal(InterfaceModal.ChangeOwner);
    emit("change_owner", interfaceObj.id);
  }, [interfaceObj, setCurrentInterface, setCurrentModal, emit]);

  const handleShareInterface = useCallback(() => {
    setCurrentInterface(interfaceObj);
    setCurrentModal(InterfaceModal.Share);
    emit("share", interfaceObj.id);
  }, [interfaceObj, setCurrentInterface, setCurrentModal, emit]);

  const changeOwnerTooltipText = useMemo(() => {
    if (permissions.isLoading) {
      return "";
    }
    return permissions.hasTeams
      ? "You don't have permission to change the owner of this interface"
      : "This feature is not available for personal accounts";
  }, [permissions.hasTeams, permissions.isLoading]);

  const disabledShareMessage = useMemo(() => {
    if (permissions.isLoading) {
      return;
    }
    return permissions.hasTeams
      ? undefined
      : "This feature is not available for personal accounts";
  }, [permissions.hasTeams, permissions.isLoading]);

  const menuItems = useMemo(
    () =>
      [
        {
          label: "Rename",
          onClick: handleRenameInterface,
          icon: "actionEdit",
          disabled: !permissions.canRename,
          tooltipText: permissions.isLoading
            ? undefined
            : "You don't have permission to rename this interface",
        },
        {
          label: "Share form",
          icon: "personAdd",
          onClick: handleShareInterface,
          disabled: !!disabledShareMessage,
          tooltipText: disabledShareMessage,
        },
        // TODO: uncomment this code when the Interface API fully supports cloning
        // {
        //   disabled: !permissions.canDuplicate,
        //   icon: "actionCopy",
        //   label: "Clone",
        //   onClick: handleDuplicateInterface,
        //   tooltipText: permissions.isLoading
        //     ? undefined
        //     : "You don't have permission to clone this interface",
        // },
        isMPFEnabled && {
          disabled: !permissions.canMove,
          icon: "actionMoveToFolder",
          label: tAction("move-to-folder"),
          onClick: handleMoveInterface,
        },
        {
          label: tAction("change-owner"),
          onClick: handleChangeInterfaceOwner,
          icon: "arrowConnect",
          disabled: !permissions.canChangeOwner,
          tooltipText: changeOwnerTooltipText,
        },
        { divider: true, label: "divider" },
        onCopyToAnotherAccount && {
          icon: "arrowExit",
          label: tAction("copy-to-another-account"),
          onClick: () => {
            onCopyToAnotherAccount(interfaceObj);
          },
        },
        onCopyToAnotherAccount && {
          divider: true,
          label: "divider",
        },
        {
          disabled: !permissions.canDelete,
          hasWarning: true,
          icon: "actionTrash",
          label: tAction("delete"),
          onClick: handleDeleteInterface,
          tooltipText: permissions.isLoading
            ? undefined
            : "You don't have permission to delete this interface",
        },
      ].filter(Boolean) as KebabMenuItem[],
    [
      changeOwnerTooltipText,
      disabledShareMessage,
      handleChangeInterfaceOwner,
      handleDeleteInterface,
      handleMoveInterface,
      handleRenameInterface,
      handleShareInterface,
      isMPFEnabled,
      onCopyToAnotherAccount,
      interfaceObj,
      permissions.canChangeOwner,
      permissions.canDelete,
      permissions.canMove,
      permissions.canRename,
      permissions.isLoading,
      tAction,
    ],
  );

  return {
    changeOwnerTooltipText,
    disabledShareMessage,
    handleShareInterface,
    menuItems,
    permissions,
  };
}
